'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

import { useLazyQuery } from '@lichtblick/apollo';
import {
  ApprovalState,
  GetWorkGroup_V1Query,
  GetWorkOrderAppointmentsWorkGroupPayloadModel,
  QueryGetWorkGroup_V1Args,
} from '@lichtblick/types';
import { getWorkgroupStatus, useLogoutOnApolloError } from '@lichtblick/utils';

import { GET_WORK_GROUP } from '../../../apps/qm-web/graphql/qm/group';
import { WorkgroupCard } from '../WorkgroupCard';

type WorkgroupMapperProps = {
  highlightedWorkGroupId?: string | null;
  workGroups: GetWorkOrderAppointmentsWorkGroupPayloadModel[];
  workOrderId?: string;
};

type WorkGroupWithItems = GetWorkOrderAppointmentsWorkGroupPayloadModel & {
  workItems: NonNullable<GetWorkGroup_V1Query['getWorkGroup_V1']['data']>['items'];
};

type Counter = { count: number; link: string };

export const WorkgroupMapper = ({ highlightedWorkGroupId, workGroups, workOrderId }: WorkgroupMapperProps) => {
  const router = useRouter();
  const [workGroupsWithItems, setWorkGroupsWithItems] = useState<WorkGroupWithItems[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [getWorkGroup, { error: getWorkGroupError }] = useLazyQuery<GetWorkGroup_V1Query, QueryGetWorkGroup_V1Args>(
    GET_WORK_GROUP,
    {
      fetchPolicy: 'network-only',
    },
  );

  useLogoutOnApolloError(getWorkGroupError);

  useEffect(() => {
    const fetchWorkItems = async () => {
      if (workGroups.length === 0) {
        setIsLoading(false);

        return;
      }

      setIsLoading(true);

      const promises = workGroups.map(async (group) => {
        if (!group) {
          return null;
        }

        try {
          const result = await getWorkGroup({
            variables: {
              input: {
                workGroupId: group.id,
              },
            },
          });

          return {
            ...group,
            workItems: result.data?.getWorkGroup_V1?.data?.items || [],
          } as WorkGroupWithItems;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(`Error fetching work items for group ${group.id}:`, error);

          return {
            ...group,
            workItems: [],
          } as WorkGroupWithItems;
        }
      });

      const results = await Promise.all(promises);
      const validGroups = results.filter((g): g is WorkGroupWithItems => g !== null);

      setWorkGroupsWithItems(validGroups);

      // Wait for next frame to ensure React has rendered the cards
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsLoading(false);
        });
      });
    };

    fetchWorkItems();
  }, [workGroups, getWorkGroup]);

  return (
    <>
      {isLoading
        ? workGroups.map((group) => <Skeleton height={'8rem'} key={group?.id || Math.random()} />)
        : workGroupsWithItems?.map((group) => {
            if (!group || !group.workItems) {
              return null;
            }

            let status:
              | {
                  counter: Counter;
                  type: ApprovalState;
                }
              | undefined = undefined;

            const counts: Record<ApprovalState, number> = {
              [ApprovalState.ServerApproved]: 0,
              [ApprovalState.Open]: 0,
              [ApprovalState.Rejected]: 0,
              [ApprovalState.Resubmitted]: 0,
              [ApprovalState.Pending]: 0,
              [ApprovalState.ClientApproved]: 0,
              [ApprovalState.Undoed]: 0,
            };

            group.workItems?.forEach((el) => {
              const key = el.approvalState as keyof typeof counts;

              counts[key]++;
            });

            if (counts[ApprovalState.ServerApproved] === group.workItems.length) {
              status = { type: ApprovalState.ServerApproved, counter: { count: 0, link: '' } };
            } else if (counts[ApprovalState.Open] === group.workItems.length) {
              status = { type: ApprovalState.Open, counter: { count: 0, link: '' } };
            } else if (
              counts[ApprovalState.Rejected] > 0 ||
              counts[ApprovalState.Resubmitted] > 0 ||
              counts[ApprovalState.Pending] > 0
            ) {
              let type: ApprovalState = ApprovalState.Pending;

              if (counts[ApprovalState.Rejected] > 0) {
                type = ApprovalState.Rejected;
              }

              if (counts[ApprovalState.Resubmitted] > 0) {
                type = ApprovalState.Resubmitted;
              }

              const count =
                counts[ApprovalState.Rejected] || counts[ApprovalState.Resubmitted] || counts[ApprovalState.Pending];

              status = getWorkgroupStatus(group.id, group.workItems, type, count);
            } else {
              status = { type: ApprovalState.Open, counter: { count: 0, link: '' } };
            }

            return (
              <WorkgroupCard
                isHighlighted={highlightedWorkGroupId === group.id.toString()}
                key={`${group.id}-${group.name ?? 'untitled'}`}
                onBarClick={(id) =>
                  router.push(workOrderId ? `orders/${workOrderId}/items/${group.id}/${id}` : `items/${group.id}/${id}`)
                }
                onChipClick={(link: string) => router.push(link)}
                status={status}
                title={group.name ?? ''}
                workGroupId={group.id}
                workItems={group.workItems ?? []}
              />
            );
          })}
    </>
  );
};
